import { Component } from './Component.js';
import { Constructor } from './types.js';
import { type DebugLogger, NOOP_DEBUG_LOGGER } from './debug/types.js';

const ENTITY_INITIALIZE_INTERNAL_KEY = Symbol.for('gotti.entity.initialize.internal');

// typed constructor alias for components
type ComponentCtor<T extends Component = Component> = new (...args: any[]) => T;

/**
 * Base class for ECS entities.
 */
export abstract class Entity<InitParamsType = any> {
	protected readonly _on_after_destroy_listeners : Array<(options?: any) => void> = [];
	protected readonly _on_before_destroy_listeners : Array<(options?: any) => void> = [];
	protected readonly listeners : {[key: string]: Array<(payload: any) => void> } = {};
	public id: string | number;
	public components: {[key: string]: Component | Component } = {};
	// optimization: track components by their constructor identity for fast lookups
	// Keyed by the component constructor type to avoid using the broad `Function` type.
	private byCtor: Map<ComponentCtor, Component | Component[]> = new Map();
	public attributes: {[name: string]: any} = {};
	public attributeGetters: Array<[string, (arg?: any) => any]> = [];
	private attributeGetterLookup: {[key: string]: (arg?: any) => any} = {};
	// all get bound/set in EntityManager.initializeEntity
	public $api : Record<string, any>;
	public $gameData : any;
	public $initializeEntity: <InitNewEntityParamsType>(entity: Entity<InitNewEntityParamsType> , data?: InitNewEntityParamsType) => Entity<InitNewEntityParamsType>;
	public $destroyEntity: (entity: Entity, options?: any) => void;

	/**
	 * Debug logger for this entity.
	 * Use this instead of console.log for debug output that appears in the debug UI.
	 * Can be called directly as a function or with .log/.info/.warn/.error methods.
	 * @example
	 * this.$debugLogger('Entity updated', this.id);
	 * this.$debugLogger.info('Component added');
	 * this.$debugLogger.group('position').log(x, y); // Grouped logs replace each other
	 */
	public $debugLogger: DebugLogger = NOOP_DEBUG_LOGGER;

	private _destroyed : boolean = false;
	private _destroying : boolean = false;

	get destroyed() {
		return this._destroyed;
	}

	get destroying() {
		return this._destroying;
	}

	get isAlive() {
		return !this.destroyed && !this.destroying;
	}

	public tryComponent<T extends Component>(component: new (...args: any[]) => T, cb: (component: T) => void) : T | null {
		// Primary fast path: direct lookup by constructor identity
		const ctorFn = component as unknown as ComponentCtor<T>;
		const entry = this.byCtor.get(ctorFn) as T | T[] | undefined;
		if(entry) {
			if(Array.isArray(entry)) {
				for(const inst of entry) {
					if(inst instanceof component) {
						cb(inst as T);
						return inst as T;
					}
				}
			} else {
				cb(entry as T);
				return entry as T;
			}
		}
		// Fallback: support parent constructors or cases where exact constructor wasn't registered
		const found = Object.values(this.components).find(c => c instanceof component) as T | undefined;
		if(found) {
			cb(found);
			return found;
		}
		return null;
	}
	

	constructor(id: string | number) {
			this.id = id;
			return this;
	}

	public on(event: string, cb: (payload: any) => void) {
			if(!(event in this.listeners)) {
					this.listeners[event] = [cb];
			} else {
					this.listeners[event].push(cb);
			}
	}
	public once(event: string, cb: (payload: any) => void) {
			const newCb = (payload) => {
					cb(payload);
					this.off(event, newCb);
			}
			this.on(event, newCb);
	}
	public off(event: string, cb?: (payload: any) => void) : boolean {
			if(event in this.listeners) {
					if(cb) {
									const idx = this.listeners[event].findIndex(c => c === cb);
									if(idx > -1) {
											this.listeners[event].splice(idx, 1);
											if(!this.listeners[event].length) {
													delete this.listeners[event];
											}
											return true;
									}
					} else {
							if(event in this.listeners) {
									delete this.listeners[event];
									return true;
							}
					}
			}
				
			return false;
	}
	public emit(event: string, payload?: any) {
		this.listeners[event]?.forEach(cb => cb(payload))
	}

	/**
	 * Entity lifecycle initializer.
	 *
	 * IMPORTANT: Do not call this directly. Systems should always create entities
	 * via `this.initializeEntity(entity, data)` so the framework can correctly wire
	 * `$api`, `$gameData`, component hooks, and lifecycle handlers.
	 *
	 * This method is intentionally `protected` to prevent direct external calls.
	 */
	protected abstract initialize(data?: InitParamsType) : void;

	/**
	 * Internal framework-only initializer entrypoint.
	 * Guarded by an internal symbol key to emulate friend-private access.
	 */
	public __initializeForSystem(data: InitParamsType | undefined, key: symbol): void {
		if (key !== ENTITY_INITIALIZE_INTERNAL_KEY) {
			throw new Error('Do not call Entity.initialize directly. Use this.initializeEntity(entity, data) from a System.');
		}
		this.initialize(data);
	}

	public onBeforeDestroyed(listener: (options?: any) => void) {
		if(this._destroying) {	
			listener(undefined);
		} else {
			this._on_before_destroy_listeners.push(listener);
		}
	}
	
	public onAfterDestroyed(listener: (options?: any) => void) {
		if(this._destroyed) {	
			listener(undefined);
		} else {
			this._on_after_destroy_listeners.push(listener);
		}
	}


	/**
	 * Adds the component to an Entity, giving it all the functionality from the methods defined.
	 * @param component
	 * @returns {Entity}
	 */
	public addComponent(component: Component) {
			if(this.hasComponent(component.name)) {
				throw `Entity ${this.id} trying to add ${component.name} twice `;
			}
			component.$api = this.$api;
			component.$gameData = this.$gameData;
			this.components[component.name] = component;

			// maintain byCtor map keyed by constructor identity
			const ctorFn = component.constructor as unknown as ComponentCtor;
			const existing = this.byCtor.get(ctorFn);
			if(!existing) {
				this.byCtor.set(ctorFn, component);
			} else if(Array.isArray(existing)) {
				existing.push(component);
			} else {
				this.byCtor.set(ctorFn, [existing, component]);
			}

			for(let i = 0; i < component.componentMethods.length; i++) {
				const propertyName = component.componentMethods[i];
				if (this[propertyName] !== undefined) {
					throw (`Duplicated property ${propertyName} names in component ${component.name} attached to an entity. Component properties must be unique`);
				}
				if (component[propertyName] instanceof Function) {
					this[propertyName] = component[propertyName].bind(component);
				}
			}
			component.emit = this.emit.bind(this);
			// attach setAttribute to component
			component.setAttribute = this.setAttribute.bind(this);
			component.setAttributeGetter = this.setAttributeGetter.bind(this);
			component.removeAttribute = this.removeAttribute.bind(this);
	
			if(component.dependencies) {
				for(const key in component.dependencies) {
					const konstructor = component.dependencies[key];
					const found = Object.values(this.components).find(c => c.constructor === konstructor);
					if(found) {
						if(!component.$deps) {
							component.$deps = {};
						}
						component.$deps[key] = found;
					} else {
						throw new Error(`Could not find dependency for key: ${key}`)
					}
				}
			/*
				const missingDependencies = component.dependentComponents.filter(c => !this.hasComponent(c));
				if(missingDependencies.length) {
						throw new Error(`Component ${component.name} is missing dependent components: ${missingDependencies.join(', ')}\n Ensure you add these components to the entity before adding the current component.`)
				}*/
		}
			component.entity = this;
			component.entityId = this.id;
			component.onAdded(this);
	}

    protected removeAttribute(key: string) {
			if(this.attributeGetterLookup[key]) {
				delete this.attributeGetterLookup[key];
				for(let i = 0; i < this.attributeGetters.length; i++) {
					if(this.attributeGetters[i][0] === key) {
						this.attributeGetters.splice(i, 1);
						return;
					}
				}
        throw new Error(`Attribute getter for key ${key} not found in attributeGetters array`);
			}
      delete this.attributes[key];
    }

    protected setAttribute(key: string, value: any) {
			this.attributes[key] = value;
    }

    protected setAttributeGetter(key: string, value: (arg: any) => any) {
			if(this.attributeGetterLookup[key]) {
				throw new Error(`Trying to set multiple attribute getters for the same key ${key}`)
			}
			this.attributeGetterLookup[key] = value;
			this.attributeGetters.push([key, value]);
    }

		public getComponentByConstructor<T extends Component>(component: new (...args: any[]) => T) : T | undefined {
			const ctorFn = component as unknown as ComponentCtor<T>;
			const entry = this.byCtor.get(ctorFn) as T | T[] | undefined;
			if(entry) {
				if(Array.isArray(entry)) {
					for(const inst of entry) {
						if(inst instanceof component) return inst as T;
					}
					return undefined;
				}
				return entry as T;
			}
			// fallback: instanceof scan (handles parent constructors / subclasses)
			return Object.values(this.components).find(c => c instanceof component) as T | undefined;
		}

	// overload: pass a constructor to get by constructor, or a name to get by component name
	public getComponent<T extends Component>(arg: Constructor<T> | string | number): T | undefined{
		// constructor case: use fast-path lookup
		if(typeof arg === 'function' && arg.prototype instanceof Component) {
			return this.getComponentByConstructor(arg as Constructor<T>);
		}
		// otherwise treat as component name
		return this.components[arg as string | number] as any;
	}

	public getAttribute(key: string) {
		if(this.attributeGetterLookup[key]) {
			return this.attributeGetterLookup[key]();
		}
		return this.attributes[key];
	}

    public getAttributes() {
			const len = this.attributeGetters.length;
			for(let i = 0; i < len; i++) {
				const array = this.attributeGetters[i];
				this.attributes[array[0]] = array[1]();
			}
			return this.attributes;
    }


    // checks if the compnentName is referenced in the entity.
    public hasComponent(componentName: string | number){
			return this.components[componentName] !== undefined;
    }

    public removeComponent(componentName, options?: any) {
			const component = this.components[componentName];

			if(component === undefined) {
				throw `Entity ${this.id} trying to remove non existening component ${componentName} `;
			}

			for(let i = 0; i < component.componentMethods.length; i++) {
				const propertyName = component.componentMethods[i];
				if (this[propertyName] !== undefined) {
					delete this[propertyName];
				}
			}

			// remove from main components map
			delete this.components[componentName];

			// also remove from the constructor keyed lookup map
			const ctorFn = component.constructor as unknown as ComponentCtor;
			const existing = this.byCtor.get(ctorFn);
			if(existing) {
				if(Array.isArray(existing)) {
					const idx = existing.indexOf(component);
					if(idx > -1) {
						existing.splice(idx, 1);
					}
					if(existing.length === 1) {
						this.byCtor.set(ctorFn, existing[0]);
					} else if(existing.length === 0) {
						this.byCtor.delete(ctorFn);
					}
				} else if(existing === component) {
					this.byCtor.delete(ctorFn);
				}
			}

			component.onRemoved(this, options);
			delete component.$api;
    }

    public removeAllListeners(event?: string) {
        if(event && event in this.listeners) {
            delete this.listeners[event];
        } else {
            Object.keys(this.listeners).forEach(k => {
                delete this.listeners[k];
            })
        }
    }

	// Use this instead of onDestroyed for backward compatibility
	public onDestroyed(listener: (options?: any) => void) {
		if(this._destroyed) {	
			listener(undefined);
		} else {
			this._on_after_destroy_listeners.push(listener);
		}
	}

    public destroy(options?: any) {
        this._destroying = true;
        
        // Call all before destroy listeners
        this._on_before_destroy_listeners.forEach(listener => listener(options));
        this._on_before_destroy_listeners.length = 0;
        
        Object.keys(this.components).forEach(c => {
            if(!(c in this.components)) { return; }
            this.removeComponent(c, options);
        });
        this.removeAllListeners();
        
        this._destroyed = true;
        
        // Call all after destroy listeners
        this._on_after_destroy_listeners.forEach(listener => listener(options));
        this._on_after_destroy_listeners.length = 0;
    }
};

export function __getEntityInitializeInternalKey(): symbol {
	return ENTITY_INITIALIZE_INTERNAL_KEY;
}